/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useState } from 'react';
import { completeSurvey, uncompleteSurvey } from '../actions/survey';
import { SurveyModelMinData } from '../model/surveyData';
import { AnalyticsEvents } from '../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../services/analytics/AnalyticsService';

type Survey = Pick<SurveyModelMinData, '_id' | 'initiativeId'>;

interface Props {
  handleReload?: () => void;
  surveys: Survey[];
  isCompleting: boolean;
}

export const useToggleCompleteSurveys = ({ handleReload = () => {}, surveys, isCompleting }: Props) => {
  const [isSaving, setSaving] = useState(false);

  const toggleCompleteSurveys = async () => {
    setSaving(true);
    try {
      await Promise.all(
        surveys.map(async (survey) => {
          isCompleting ? await completeSurvey(survey._id) : await uncompleteSurvey(survey._id);
          trackSurveyComplete(survey, isCompleting);
        })
      );
      setSaving(false);
      handleReload();
    } catch {
      setSaving(false);
    }
  };

  return { isSaving, toggleCompleteSurveys };
};

const trackSurveyComplete = async (survey: Survey, isCompleted: boolean) => {
  const analytics = getAnalytics();
  await analytics.track(AnalyticsEvents.SurveyCompleted, {
    surveyId: survey._id,
    initiativeId: survey.initiativeId,
    isCompleted,
  });
};
